import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  backLinkClasses,
  Button,
  Card,
  Field,
  Icon,
  Page,
  PageHeader,
} from '@evogard/ui';
import {
  ZERO,
  settleCommission,
  fromColumn,
  format,
  loadFailureMessage,
  subtract,
} from '@evogard/domain';
import type {
  PlateClassification,
  Money,
  PeriodPlate,
  CommissionRule,
  CommissionTier,
} from '@evogard/domain';
import { COMMISSION_LADDERS } from '@evogard/contract';
import type {
  Period,
  LadderStep,
  WireLadderName,
  CommissionLadder,
} from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { newRowKey, useEditableRows } from '../../lib/editable-rows.js';
import { can, useSessionContext } from '../../lib/session.js';
import { PeriodField, FieldSlot, currentPeriod, periodLabel } from './common.js';

/**
 * A régua de comissão da base — quanto vale cada placa, por competência.
 *
 * É a tela que **destrava o módulo**. Sem régua declarada, `POST
 * /comercial/comissoes/apurar` responde `nao_apurada` com o motivo
 * `regua_de_comissao_nao_definida` para sempre, e nenhum dado que chegue remove
 * esse motivo: só cadastro remove. Até 28/08 `SalesTiersService` existia,
 * com teste, e não havia caminho nenhum até ele — rota, permissão e menu
 * estavam todos certos, e ninguém conferia se alguma tela chegava lá.
 *
 * ## O nome do arquivo é `LadderScreen`, e o da cobrança é `TiersScreen`
 *
 * Os dois endereços são `/<modulo>/faixas` — convenção 7, um nome por conceito.
 * O componente é que não pode repetir: `routes.tsx` importa os dois no mesmo
 * arquivo, e dois `TiersScreen` obrigariam a um apelido no import. Apelido de
 * import é como a tela errada acaba montada num endereço sem ninguém notar, e o
 * roteador não tem como acusar isso.
 *
 * ## Ela salva a régua INTEIRA, e mesmo assim NÃO substitui a coleção
 *
 * Aqui ela diverge da cobrança, e a diferença é a vigência. `PUT
 * /cobranca/faixas` apaga a régua da base e reinsere; `PUT /sales/tiers`
 * **abre uma vigência**: arquiva a reedição do mesmo mês, fecha o que estava
 * aberto no mês anterior e insere a régua nova valendo daquela competência em
 * diante.
 *
 * As duas razões estão no cabeçalho de `sales_tiers`, e são as duas que
 * esta tela precisa dizer com todas as letras:
 *
 *   · `sales_commissions.faixa_id` aponta para o degrau por estrangeira
 *     composta. Apagar deixaria um valor apurado sem explicação;
 *   · a régua de março **precisa continuar existindo** depois de a de abril
 *     entrar, senão a apuração de março deixa de ser explicável pelo degrau
 *     exato que a produziu.
 *
 * **Corrigir um mês fechado é fechar e abrir, não editar.** Quem abrir esta tela
 * em março e salvar está declarando "de março em diante é assim" — a régua
 * anterior passa a terminar em fevereiro e continua lá. Sem essa frase, alguém
 * edita achando que corrige e reescreve o passado; com ela, sabe que precisa
 * **reapurar** a competência, que é outro ato, em outra tela.
 *
 * ## São TRÊS réguas, e faltando uma o valor sai menor com cara de conta certa
 *
 * `configurada` só nasce com `common`, `base_reativacao` e `bronze` presentes, e
 * o motivo é aritmético (desde 28/09 "presente" é por perfil: as faixas e o
 * Bronze podem ser só do interno ou só do externo, e a compartilhada cobre quem
 * não tem a sua — ver `PROFILE_LADDERS` em `sales.tiers.service.ts`): sem a do Bronze, cinco placas Bronze num consultor de
 * 25 placas sairiam por R$ 200,00 em vez de R$ 275,00; sem a da base de
 * reativação, quarenta reativações valeriam R$ 0,00 em vez de R$ 400,00 — e
 * R$ 0,00 é exatamente o que essa consultora receberia se tivesse feito 24. Por
 * isso o estado `incompleta` nomeia **quais** faltam, e esta tela repete o nome:
 * "falta alguma" mandaria procurar; o que a operação precisa é saber o que
 * digitar.
 *
 * ## O EFEITO de cada degrau é calculado pela função que APURA
 *
 * Esta tela mostra, ao lado de cada degrau, quanto a régua digitada pagaria a
 * quem parasse exatamente naquele piso — e quanto vale a placa que tira o
 * consultor do degrau anterior. Não há uma linha de aritmética de comissão
 * escrita aqui: quem calcula é `settleCommission`, de `@evogard/domain`,
 * a mesma função que o servidor chama para apurar, chamada com placas
 * fictícias. Reescrever a conta "só para o preview" é como as duas contagens de
 * ranking do sistema antigo nasceram.
 *
 * Sem esse efeito à vista, a régua é uma tabela de números pequenos: a 25ª placa
 * vale R$ 250,00 no sistema antigo (ela tira o consultor de faixa nenhuma e paga
 * as 25), e a 110ª vale R$ 350,00 a mais que a 109ª. Quem digita um piso precisa
 * ver o degrau que está criando.
 *
 * ## A régua nasce VAZIA nas duas praças, e a tela diz de onde ela vem
 *
 * Uma tabela vazia sem explicação leria como "esta praça decidiu não pagar
 * comissão". O que a tela diz é onde os números de verdade estão — e eles não
 * estão num banco: no sistema antigo as faixas são **constantes no código**
 * (`src/lib/comissoes.ts`), e mudar uma exige deploy. A leitura de 28/08 as
 * transcreveu para o levantamento de remuneração; nenhuma delas é sugerida em
 * campo nenhum desta tela, porque a parte 7 decidiu que a regra vem do
 * aplicativo original **com aprovação por escrito**, e um valor sugerido é uma
 * suposição que atravessa a tela com cara de padrão da casa.
 */
export function LadderScreen() {
  const { data: context } = useSessionContext();
  const canEdit = can(context, 'sales.tiers', 'editar');
  const [period, setPeriod] = useState<Period>(currentPeriod());
  const ladderQuery = commissionuseLadder(period);
  const ladder = ladderQuery.data;

  return (
    <Page measure="cheia">
      <PageHeader
        title="Régua de comissão"
        back={
          <Link
            to="/sales/commissions"
            activeOptions={{ exact: true }}
            className={backLinkClasses()}
          >
            <Icon nome="seta-esquerda" size={16} />
            Comissões
          </Link>
        }
      >
        Vale para a base{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        A outra praça tem a régua dela — mudar aqui não muda lá.
      </PageHeader>

      <div className="max-w-xs">
        <PeriodField
          label="Competência"
          value={period}
          onChange={setPeriod}
          help="A régua tem vigência: esta é a que valia (ou passará a valer) neste mês."
        />
      </div>

      {ladderQuery.isError && (
        <Alert tone="erro" title="Não foi possível carregar a régua desta competência.">
          {ladderQuery.error.message}
        </Alert>
      )}

      {ladder && <LadderState ladder={ladder} />}

      {/* Erro sem dado nenhum: o aviso acima é a resposta inteira. Um editor
          vazio logo abaixo dele convidaria a digitar uma régua sobre uma
          competência cujo estado a tela não conseguiu ler. */}
      {ladderQuery.isPending ? (
        <Skeleton />
      ) : ladder ? (
        <LadderEditor
          /* Re-semeia ao trocar de praça E ao trocar de mês: os dois mudam a
             régua que está sendo editada, e um estado que sobrevivesse à troca
             gravaria os degraus de um mês no outro sem ninguém notar. */
          key={`${String(context?.currentTenant.id)}:${period}`}
          period={period}
          initial={ladder.estado === 'sem_regua' ? [] : ladder.rungs}
          canEdit={canEdit}
        />
      ) : null}
    </Page>
  );
}

/* ------------------------------------------------------------------------- *
 * A consulta
 * ------------------------------------------------------------------------- */

/**
 * A régua da base **na competência pedida**.
 *
 * A competência entra na chave junto do tenant. Sem ela, trocar de mês
 * devolveria a régua do mês anterior do cache — e a régua de março existe
 * justamente para reapurar março com o que valia em março.
 */
function commissionuseLadder(period: Period) {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  return useQuery({
    queryKey: ['comercial', 'faixas', tenantId, period],
    queryFn: async () => {
      const r = await api.sales.tiers({ query: { period } });
      if (r.status !== 200) {
        /*
         * "desta competência", e não "desta base": o ramo de 404 de
         * `loadFailureMessage` já acrescenta "nesta base" ao fim da
         * frase, e o assunto que trouxesse a base junto sairia como "a régua
         * desta base nesta base".
         */
        throw new Error(loadFailureMessage(r.status, 'a régua desta competência'));
      }
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId && previousQuery?.queryKey[3] === period
        ? previous
        : undefined,
  });
}

/* ------------------------------------------------------------------------- *
 * O vocabulário das três réguas
 * ------------------------------------------------------------------------- */

/**
 * O nome de cada régua na tela, escrito uma vez.
 *
 * O `satisfies Record<WireLadderName, …>` faz uma régua nova quebrar a
 * compilação em vez de aparecer como `bronze` cru numa lista e "Plano Bronze"
 * na outra — e aqui o identificador cru apareceria dentro de uma frase sobre
 * quanto alguém recebe.
 *
 * `whatPays` não é decoração: é a diferença que faz a régua incompleta ser
 * perigosa. As três não são a mesma conta com números diferentes, e quem
 * cadastra precisa saber o que está declarando antes de digitar o valor.
 */
// @valor
const LADDER = {
  comum: {
    nome: 'Consultor interno e externo',
    whatPays:
      'Conta venda, troca e Plano Bronze; reativação não conta. A faixa é escolhida ' +
      'pelo total de placas do mês, e o valor dela é aplicado sobre as placas que não ' +
      'são Bronze. Vale para o interno e o externo que não tiverem régua própria.',
    withoutEla:
      'Sem esta régua não há faixa alcançável para quem não tem régua própria, e a ' +
      'parte das placas comuns sai zerada.',
    shared: null,
  },
  interno: {
    nome: 'Só consultor interno',
    whatPays:
      'Mesma conta da régua “Consultor interno e externo”, com degraus só do interno. ' +
      'Declarada, ela vale no lugar daquela para todo consultor interno.',
    withoutEla:
      'O consultor interno é pago pelas faixas da régua “Consultor interno e externo”.',
    shared: 'comum',
  },
  externo: {
    nome: 'Só consultor externo',
    whatPays:
      'Mesma conta da régua “Consultor interno e externo”, com degraus só do externo. ' +
      'Declarada, ela vale no lugar daquela para todo consultor externo.',
    withoutEla:
      'O consultor externo é pago pelas faixas da régua “Consultor interno e externo”.',
    shared: 'comum',
  },
  base_reativacao: {
    nome: 'Base de reativação',
    whatPays:
      'Régua própria de quem tem o perfil “base de reativação” no cadastro: só ' +
      'reativação conta, e o Plano Bronze não se aplica.',
    withoutEla:
      'Sem esta régua, quem fez quarenta reativações recebe R$ 0,00 — que é ' +
      'exatamente o que receberia com 24, e as duas coisas ficam indistinguíveis.',
    shared: null,
  },
  bronze: {
    nome: 'Plano Bronze — interno e externo',
    whatPays:
      'Não é degrau: cada placa Bronze paga este valor SEMPRE, inclusive abaixo de ' +
      'todo piso. E ela ainda conta para escolher a faixa das outras. Vale para o ' +
      'interno e o externo que não tiverem Bronze próprio.',
    withoutEla:
      'Sem esta régua, a placa Bronze sai por R$ 0,00 e continua empurrando a faixa — ' +
      'o total fica menor com cara de conta certa.',
    shared: null,
  },
  bronze_interno: {
    nome: 'Plano Bronze — só interno',
    whatPays:
      'O valor da placa Bronze do consultor interno. Declarado, vale no lugar do ' +
      'Bronze de interno e externo.',
    withoutEla: 'A placa Bronze do consultor interno paga o Bronze de interno e externo.',
    shared: 'bronze',
  },
  bronze_externo: {
    nome: 'Plano Bronze — só externo',
    whatPays:
      'O valor da placa Bronze do consultor externo. Declarado, vale no lugar do ' +
      'Bronze de interno e externo.',
    withoutEla: 'A placa Bronze do consultor externo paga o Bronze de interno e externo.',
    shared: 'bronze',
  },
} as const satisfies Record<
  WireLadderName,
  {
    nome: string;
    whatPays: string;
    withoutEla: string;
    /** a régua que paga no lugar desta quando ela está vazia — nulo se não há */
    shared: WireLadderName | null;
  }
>;

/** A régua é de Bronze — um valor por placa Bronze, sem piso — e não de faixas? */
function isBronzeLadder(ladder: WireLadderName): boolean {
  return (
    ladder === 'bronze' || ladder === 'bronze_interno' || ladder === 'bronze_externo'
  );
}

/* ------------------------------------------------------------------------- *
 * O estado da competência
 * ------------------------------------------------------------------------- */

/**
 * Os três estados, cada um com o que ele significa para a apuração.
 *
 * A união discriminada do contrato existe para "esta praça não declarou régua" e
 * "a régua existe e está assim" não serem a mesma resposta. Desenhar os três com
 * o mesmo aviso desfaria isso na tela.
 */
function LadderState({ ladder }: { ladder: CommissionLadder }) {
  if (ladder.estado === 'sem_regua') {
    return (
      <Alert
        tone="alerta"
        title={`Esta praça não tem régua de comissão em ${periodLabel(ladder.period)}.`}
      >
        <p className="mt-1">
          Nenhum degrau alcança esta competência. Não é falha de carregamento nem filtro
          escondendo alguma coisa: é o que a casa sabe hoje — e enquanto for assim,{' '}
          <strong className="font-medium">apurar recusa</strong> esta competência inteira
          e nada é gravado.
        </p>
        <p className="mt-2">
          Pode ser também uma <strong>lacuna entre vigências</strong>: a régua da praça
          existe em outros meses e não alcança este. Ninguém sabe se ela começou depois ou
          se o mês foi esquecido, e as duas coisas têm a mesma forma — por isso a apuração
          recusa em vez de pagar zero.
        </p>
        <p className="mt-2">{LADDER_SOURCE}</p>
      </Alert>
    );
  }

  if (ladder.estado === 'incompleta') {
    return (
      <Alert
        tone="alerta"
        title={`Faltam ${ladder.missing.length === 1 ? 'os degraus de uma régua' : `os degraus de ${ladder.missing.length} réguas`} em ${periodLabel(ladder.period)}.`}
      >
        <p className="mt-1">
          A apuração precisa de régua para todo perfil, e por um motivo aritmético: com
          uma faltando, o valor sai{' '}
          <strong className="font-medium">menor, com cara de conta certa</strong>. Falta:
        </p>
        <ul className="mt-2 flex flex-col gap-2">
          {ladder.missing.map((nome) => {
            const info: (typeof LADDER)[WireLadderName] = LADDER[nome];
            return (
              <li key={nome}>
                <p className="font-medium">{info.nome}</p>
                <p className="mt-0.5 text-texto-suave">
                  {info.shared === null
                    ? info.withoutEla
                    : /* A que falta é a própria SÓ porque o outro perfil já tem a
                         dele — e a compartilhada também está vazia. Declarar
                         qualquer uma das duas resolve. */
                      `O perfil não tem régua própria, e “${LADDER[info.shared].nome}” também está vazia. Declare uma das duas.`}
                </p>
              </li>
            );
          })}
        </ul>
        <p className="mt-2">
          Enquanto faltar qualquer uma,{' '}
          <strong className="font-medium">apurar recusa</strong> esta competência e nada é
          gravado.
        </p>
      </Alert>
    );
  }

  return (
    <Alert
      tone="sucesso"
      title={`A régua está completa em ${periodLabel(ladder.period)}.`}
    >
      <p className="mt-1">
        Interno, externo e base de reativação têm por qual régua ser pagos, e a apuração
        desta competência não é mais recusada por falta de régua. Ela ainda pode recusar
        por outros motivos — venda sem classificação, consultor sem régua declarada no
        cadastro —, e cada um deles aparece nomeado em Comissões.
      </p>
    </Alert>
  );
}

/**
 * De onde os números vêm, escrito uma vez.
 *
 * `sales_tiers` nasce vazia nas duas praças. E, ao contrário da régua da
 * cobrança, os valores desta **não estão num banco**: no sistema antigo as
 * faixas são constantes no código, e mudar uma faixa exige deploy — é isto que a
 * P4 desfaz ao torná-las dado por base e por competência.
 */
const LADDER_SOURCE =
  'Os valores desta praça vêm do sistema antigo, onde as faixas são constantes ' +
  'escritas no código — mudar uma exige deploy, e CE e GO não podem divergir. A ' +
  'leitura de 28/08 as transcreveu no levantamento de remuneração, e elas esperam ' +
  'o sim por escrito do responsável comercial. Nenhum campo desta tela sugere um ' +
  'valor: uma régua que nascesse aqui pagaria gente real sob números que ninguém ' +
  'decidiu.';

/* ------------------------------------------------------------------------- *
 * O editor
 * ------------------------------------------------------------------------- */

/**
 * Uma linha em edição.
 *
 * Os números são **texto** enquanto se digita, pelo mesmo motivo da régua da
 * cobrança: um `number` no estado obriga a escolher o que fazer com o campo
 * vazio e com o `-` sozinho de quem está digitando `-3`, e a escolha silenciosa
 * é `0` — que é um piso de régua, não um "não preenchido".
 *
 * `rowId` existe porque o piso é editável: usá-lo como `key` do React faria
 * a linha remontar a cada tecla, e o cursor sairia do campo.
 */
interface EditingRow {
  rowId: string;
  ladder: WireLadderName;
  platesMin: string;
  amountPerPlate: string;
}

function forRows(rungs: readonly LadderStep[]): EditingRow[] {
  return rungs.map((d) => ({
    rowId: newRowKey('degrau'),
    ladder: d.ladder,
    platesMin: String(d.platesMin),
    amountPerPlate: d.amountPerPlate,
  }));
}

/** O que vai para o servidor — sem `rowId`, que é só do React. */
function forBody(rows: readonly EditingRow[]) {
  return rows.map((l) => ({
    ladder: l.ladder,
    platesMin: Number(l.platesMin),
    amountPerPlate: l.amountPerPlate.trim(),
  }));
}

/**
 * A FORMA do que foi digitado — e só a forma.
 *
 * Este é o único julgamento que esta tela faz sobre o que a pessoa escreveu, e
 * ele não é uma regra: "isto não é um número" é diferente de "o banco recusa
 * este número". Piso negativo, sobreposição de vigência, Bronze com piso e
 * degrau repetido continuam sendo recusados pelo **servidor**, e a tela mostra a
 * frase dele — é a mesma disciplina que a régua da cobrança aplica à
 * sobreposição.
 *
 * A forma precisa ser conferida aqui por uma razão prática: `moneySchema` é
 * um `regex` de transporte, e a recusa dele não é uma frase em pt-BR — é um erro
 * de validação de esquema, que na tela apareceria como um 400 sem nome. Quem
 * digita `10,00` merece a vírgula apontada, não isso.
 */
function shapeProblem(row: EditingRow): string | null {
  const floor = row.platesMin.trim();
  if (floor === '' || !/^-?\d+$/.test(floor)) {
    return 'O piso é um número inteiro de placas.';
  }
  const value = row.amountPerPlate.trim();
  if (value === '') return 'Falta o valor por placa.';
  if (!/^-?\d+(\.\d{1,2})?$/.test(value)) {
    return 'O valor vai com ponto e até duas casas — 10 ou 10.00, nunca 10,00.';
  }
  return null;
}

function LadderEditor({
  period,
  initial,
  canEdit,
}: {
  period: Period;
  initial: readonly LadderStep[];
  canEdit: boolean;
}) {
  const { rows, setRows, updated, reseed } = useEditableRows(initial, forRows, forBody);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const withProblem = rows.filter((l) => shapeProblem(l) !== null).length;

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.sales.saveLadder({
        body: { period, rungs: forBody(rows) },
      });
      /*
       * A mensagem do servidor, sempre. As recusas mais importantes daqui vêm
       * do banco traduzido — a sobreposição de vigências nomeia o degrau e as
       * duas vigências que se encavalaram, e o valor negativo diz que um sinal
       * a mais numa digitação pagaria ao contrário. Uma mensagem genérica
       * jogaria fora exatamente a informação que resolve o problema.
       */
      return ok(r, 200, 'Não foi possível salvar a régua.');
    },
    onSuccess: (ladder) => {
      setError(null);
      setDone(true);
      /*
       * Re-semeia do que o SERVIDOR devolveu: os `id` que a comissão vai gravar,
       * os valores já normalizados pelo `numeric(12,2)` e a ordenação dele.
       * Manter o estado local seria a tela afirmando que gravou o que ela tinha,
       * e não o que ficou lá.
       */
      reseed(ladder.estado === 'sem_regua' ? [] : ladder.rungs);
    },
    onError: (e: Error) => {
      setDone(false);
      setError(e.message);
    },
  });

  function change(keyLocal: string, patch: Partial<EditingRow>) {
    setDone(false);
    setRows((current) =>
      current.map((l) => (l.rowId === keyLocal ? { ...l, ...patch } : l)),
    );
  }

  function add(ladder: WireLadderName) {
    setDone(false);
    setRows((current) => [
      ...current,
      {
        rowId: newRowKey('degrau'),
        ladder,
        /* O Bronze não é degrau: o piso dele é 0, e o `check`
           `sales_tiers_bronze_without_floor_ck` é quem o prende ali. */
        platesMin: isBronzeLadder(ladder) ? '0' : '',
        amountPerPlate: '',
      },
    ]);
  }

  function discard(keyLocal: string) {
    setDone(false);
    setRows((current) => current.filter((l) => l.rowId !== keyLocal));
  }

  const rule = editorRule(rows);

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      {error && (
        <Alert tone="erro" title="A régua não foi gravada.">
          {error}
        </Alert>
      )}

      {done && !updated && (
        <Alert tone="sucesso" title="A vigência foi aberta.">
          <p className="mt-1">
            De {periodLabel(period)} em diante, a régua desta base é a que está abaixo. A
            anterior foi fechada no mês antes deste e continua lá, explicando a comissão
            que ela pagou. As competências já apuradas{' '}
            <strong className="font-medium">não mudaram de valor</strong>: para isso é
            preciso apurar de novo.
          </p>
        </Alert>
      )}

      {rule.tipo === 'recusada' && (
        <Alert tone="alerta" title="A apuração recusaria esta régua.">
          <p className="mt-1">
            Quem disse isso foi a própria função que apura, com os números que estão nesta
            tela: <strong className="font-medium">{rule.because}</strong>
          </p>
          <p className="mt-2">
            Quem decide de verdade é o servidor, ao salvar — esta é a mesma conta rodando
            aqui para o problema não esperar pela gravação.
          </p>
        </Alert>
      )}

      {COMMISSION_LADDERS.map((nome) => (
        <LadderGroup
          key={nome}
          ladder={nome}
          rows={rows.filter((l) => l.ladder === nome)}
          rule={rule.tipo === 'pronta' ? rule.rules[nome] : null}
          canEdit={canEdit}
          onChange={change}
          toDiscard={discard}
          toAdd={() => add(nome)}
        />
      ))}

      <div className="flex flex-wrap items-center gap-3">
        {canEdit && (
          <Button
            type="submit"
            loading={save.isPending}
            disabled={rows.length === 0 || !updated || withProblem > 0}
          >
            Abrir a vigência de {periodLabel(period)}
          </Button>
        )}
        {canEdit && updated && (
          <Button
            variant="discreto"
            type="button"
            onClick={() => {
              reseed(initial);
              setError(null);
              setDone(false);
            }}
          >
            Descartar alterações
          </Button>
        )}
        {updated && (
          <span className="text-xs font-medium text-alerta">
            Há alterações que ainda não foram gravadas.
          </span>
        )}
        {withProblem > 0 && (
          <span className="text-xs font-medium text-erro">
            {withProblem === 1
              ? 'Uma linha ainda não tem número que dê para mandar.'
              : `${withProblem} linhas ainda não têm número que dê para mandar.`}
          </span>
        )}
      </div>

      <p className="text-xs text-texto-suave">
        Salvar grava mesmo que nada tenha mudado nos números: não há comparação com o
        estado anterior, porque uma igualdade frouxa descartaria uma edição de verdade em
        silêncio. O preço é uma vigência a mais na história, e ela é o registro de que
        alguém salvou.
      </p>
    </form>
  );
}

/* ------------------------------------------------------------------------- *
 * Um grupo — os degraus de uma das três réguas
 * ------------------------------------------------------------------------- */

function LadderGroup({
  ladder,
  rows,
  rule,
  canEdit,
  onChange,
  toDiscard,
  toAdd,
}: {
  ladder: WireLadderName;
  rows: readonly EditingRow[];
  rule: CommissionRule | null;
  canEdit: boolean;
  onChange: (keyLocal: string, patch: Partial<EditingRow>) => void;
  toDiscard: (keyLocal: string) => void;
  toAdd: () => void;
}) {
  const info: (typeof LADDER)[WireLadderName] = LADDER[ladder];
  const isBronze = isBronzeLadder(ladder);
  /* O Bronze é um valor, não uma escada: uma linha só por vigência, e é o
     `check` do banco que o prende no piso 0. Oferecer "acrescentar" ali
     convidaria a uma segunda linha que o `EXCLUDE` recusa depois de digitada. */
  const canAdd = canEdit && (!isBronze || rows.length === 0);

  const sorted = [...rows].sort(
    (a, b) => Number(a.platesMin || '0') - Number(b.platesMin || '0'),
  );

  return (
    <Card
      title={info.nome}
      descricao={info.whatPays}
      actions={
        canAdd ? (
          <Button variant="contorno" size="sm" type="button" onClick={toAdd}>
            {isBronze ? 'Declarar o valor do Bronze' : 'Acrescentar degrau'}
          </Button>
        ) : null
      }
      bleed
    >
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-texto-suave">
          {info.shared === null ? (
            <>
              Nenhum degrau declarado. {info.withoutEla} Enquanto faltar, a apuração desta
              competência é recusada inteira e nada é gravado.
            </>
          ) : (
            <>
              Sem régua própria. {info.withoutEla} Se aquela também faltar, a apuração
              desta competência é recusada inteira.
            </>
          )}
        </p>
      ) : (
        <ol>
          {sorted.map((row) => (
            <li
              key={row.rowId}
              className="border-b border-borda px-4 py-4 last:border-b-0"
            >
              <RungRow
                row={row}
                ladder={ladder}
                rule={rule}
                ladderRungs={sorted}
                canEdit={canEdit}
                onChange={(patch) => onChange(row.rowId, patch)}
                toDiscard={() => toDiscard(row.rowId)}
              />
            </li>
          ))}
        </ol>
      )}

      {isBronze && rows.length > 0 && rule && <BronzeExample rule={rule} />}
    </Card>
  );
}

/* ------------------------------------------------------------------------- *
 * A linha
 * ------------------------------------------------------------------------- */

function RungRow({
  row,
  ladder,
  rule,
  ladderRungs,
  canEdit,
  onChange,
  toDiscard,
}: {
  row: EditingRow;
  ladder: WireLadderName;
  rule: CommissionRule | null;
  ladderRungs: readonly EditingRow[];
  canEdit: boolean;
  onChange: (patch: Partial<EditingRow>) => void;
  toDiscard: () => void;
}) {
  const problem = shapeProblem(row);
  const isBronze = isBronzeLadder(ladder);

  return (
    <div className="flex flex-col gap-3">
      {/*
       * `items-start` com o "Remover" em `FieldSlot`: os dois campos têm linha
       * de ajuda embaixo, e com `items-end` o botão alinhava pela borda de
       * baixo dela — um degrau abaixo dos campos que ele remove.
       */}
      <div className="flex flex-wrap items-start gap-3">
        {!isBronze && (
          <div className="w-40">
            {/*
             * Sem `min`, e de propósito. `sales_tiers_non_negative_ck`
             * recusa piso negativo com uma frase em pt-BR que diz que um sinal a
             * mais numa digitação sairia verde na tela e pagaria ao contrário —
             * e uma tradução que ninguém consegue alcançar é uma tradução que
             * apodrece. Quem recusa é o banco; a tela deixa chegar até ele.
             */}
            <Field
              label="A partir de (placas)"
              inputMode="numeric"
              disabled={!canEdit}
              value={row.platesMin}
              onChange={(e) => onChange({ platesMin: e.target.value })}
              help="É piso, não intervalo: vale a maior faixa que o total do mês alcança."
            />
          </div>
        )}
        <div className="w-40">
          <Field
            label={isBronze ? 'R$ por placa Bronze' : 'R$ por placa'}
            inputMode="decimal"
            placeholder="0.00"
            disabled={!canEdit}
            value={row.amountPerPlate}
            onChange={(e) => onChange({ amountPerPlate: e.target.value })}
            error={problem}
            help={isBronze ? 'Pago por placa Bronze, sempre.' : undefined}
          />
        </div>
        {canEdit && (
          <FieldSlot>
            <Button variant="discreto" size="sm" type="button" onClick={toDiscard}>
              Remover
            </Button>
          </FieldSlot>
        )}
      </div>

      {isBronze ? (
        <p className="text-sm text-texto-suave">
          O Bronze <strong className="font-medium text-texto">não tem piso</strong>: cada
          placa Bronze paga este valor mesmo com o consultor abaixo de todas as faixas — e
          ela ainda entra na contagem que escolhe a faixa das outras placas.
        </p>
      ) : (
        <RungEffect row={row} ladder={ladder} rule={rule} ladderRungs={ladderRungs} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * O efeito de um degrau
 *
 * A conta é de `settleCommission`, de `@evogard/domain` — a MESMA função
 * que o servidor chama para apurar, com placas fictícias. Nada de comissão é
 * recalculado nesta pasta: se fosse, a tela e a apuração poderiam divergir, que
 * é como o sistema antigo passou a ter duas contagens de ranking.
 * ------------------------------------------------------------------------- */

/**
 * Quantas placas fictícias a tela topa montar.
 *
 * Um piso digitado com um zero a mais construiria um milhão de objetos e
 * travaria a aba entre duas teclas. Acima do teto a tela diz que não simulou —
 * que é diferente de simular e dar um número errado.
 */
const CAP_SIMULACAO = 3000;

function platesFicticias(
  count: number,
  classification: PlateClassification,
  prefix: string,
): PeriodPlate[] {
  /* A referência precisa ser única: `settleCommission` recusa placa
     repetida, porque placa repetida empurra a faixa e paga o que ninguém
     vendeu. */
  return Array.from({ length: count }, (_, i) => ({
    reference: `${prefix}-${i}`,
    classification,
  }));
}

/**
 * A régua digitada, na forma que a apuração recebe — uma por grupo da tela —, ou
 * o motivo de não dar.
 *
 * Cada grupo simula com a SUA régua: o efeito de um degrau de "Só consultor
 * interno" é o que ele paga ao interno, e não o da compartilhada. O Bronze
 * simula sobre as faixas do perfil dele — as próprias, senão as compartilhadas,
 * que é a resolução de `PROFILE_LADDERS` no servidor. É só o exemplo da tela:
 * quem resolve a régua que paga é o servidor.
 */
type EditorRule =
  | { tipo: 'pronta'; rules: Record<WireLadderName, CommissionRule> }
  | { tipo: 'incompleta' }
  | { tipo: 'recusada'; because: string };

function editorRule(rows: readonly EditingRow[]): EditorRule {
  if (rows.length === 0 || rows.some((l) => shapeProblem(l) !== null)) {
    return { tipo: 'incompleta' };
  }

  const tiersOf = (nome: WireLadderName) =>
    rows
      .filter((l) => l.ladder === nome)
      .map((l) => ({
        platesMin: Number(l.platesMin.trim()),
        /* `fromColumn` é a fronteira com a coluna `numeric(12,2)`, e nunca
           `Number(...)`: a forma já foi conferida acima, então o `?? ZERO` é
           inalcançável — ele existe porque a assinatura devolve `Dinheiro | null`
           para o texto vazio, que `shapeProblem` já recusou. */
        amountPerPlate: fromColumn(l.amountPerPlate.trim()) ?? ZERO,
      }));

  const reactivation = tiersOf('base_reativacao');
  /* A faixa própria, senão a compartilhada — a mesma ordem do servidor. */
  const tiersFor = (own: WireLadderName) =>
    tiersOf(own).length > 0 ? tiersOf(own) : tiersOf('comum');

  const ruleWith = (
    tiers: CommissionTier[],
    bronze: WireLadderName | null,
  ): CommissionRule => ({
    tiers,
    tenantDeReactivationTiers: reactivation,
    /* Bronze ainda não declarado entra como zero SÓ para as simulações que não
       usam placa Bronze nenhuma; o exemplo do Bronze não é desenhado sem a linha
       dele, justamente para não mostrar R$ 0,00 como se fosse a régua da praça. */
    valueByPlateBronze: (bronze && tiersOf(bronze)[0]?.amountPerPlate) ?? ZERO,
  });

  /* As chaves são VALORES de `app.sales_commission_ladder`, não nomes nossos. */
  // @valor
  const rules: Record<WireLadderName, CommissionRule> = {
    comum: ruleWith(tiersOf('comum'), null),
    interno: ruleWith(tiersOf('interno'), null),
    externo: ruleWith(tiersOf('externo'), null),
    base_reativacao: ruleWith([], null),
    bronze: ruleWith(tiersOf('comum'), 'bronze'),
    bronze_interno: ruleWith(tiersFor('interno'), 'bronze_interno'),
    bronze_externo: ruleWith(tiersFor('externo'), 'bronze_externo'),
  };

  try {
    /* A validação da régua mora dentro de `settleCommission` — piso
       repetido, piso não inteiro, valor negativo. Uma chamada com nenhuma placa
       é o jeito de ouvi-la sem reescrever nenhuma das checagens dela. */
    for (const rule of Object.values(rules)) {
      settleCommission([], 'externo', rule);
    }
  } catch (e) {
    return { tipo: 'recusada', because: e instanceof Error ? e.message : String(e) };
  }

  return { tipo: 'pronta', rules };
}

/** O total que a régua digitada paga a quem fizer exatamente `placas` placas. */
function totalWith(
  plates: number,
  ladder: WireLadderName,
  rule: CommissionRule,
): Money | null {
  if (plates < 0 || plates > CAP_SIMULACAO) return null;
  try {
    if (ladder === 'base_reativacao') {
      return settleCommission(
        platesFicticias(plates, 'reativacao', 'r'),
        'base_reativacao',
        rule,
      ).total;
    }
    return settleCommission(platesFicticias(plates, 'venda', 'v'), 'externo', rule).total;
  } catch {
    /* A régua recusada já é dita no topo do formulário, com a frase da própria
       função. Repeti-la em cada linha esconderia o degrau atrás do erro. */
    return null;
  }
}

/**
 * Quanto a régua digitada paga no piso deste degrau, e quanto vale a placa que
 * chega nele.
 *
 * O segundo número é o que a tabela sozinha não mostra: a 25ª placa vale
 * R$ 250,00 no sistema antigo — ela tira o consultor de faixa nenhuma e paga as
 * 25 —, e a 110ª vale R$ 350,00 a mais que a 109ª. É o desenho da regra, não um
 * defeito; e é exatamente o tipo de degrau que faz uma venda transferida em
 * setembro mudar março.
 *
 * As placas são todas comuns, sem Bronze, e a frase diz isso: com Bronze na
 * conta o número é outro, porque o Bronze escolhe a faixa e paga por fora dela.
 */
function RungEffect({
  row,
  ladder,
  rule,
  ladderRungs,
}: {
  row: EditingRow;
  ladder: WireLadderName;
  rule: CommissionRule | null;
  ladderRungs: readonly EditingRow[];
}) {
  if (rule === null || shapeProblem(row) !== null) {
    return (
      <p className="text-sm text-texto-suave">
        O efeito deste degrau aparece quando o piso e o valor estiverem preenchidos.
      </p>
    );
  }

  const floor = Number(row.platesMin.trim());
  const noFloor = totalWith(floor, ladder, rule);
  const oneLess = totalWith(floor - 1, ladder, rule);

  if (noFloor === null) {
    return (
      <p className="text-sm text-texto-suave">
        Piso alto demais para simular aqui (o teto é {CAP_SIMULACAO} placas). O degrau
        continua valendo — o que esta tela não faz é montar a conta de exemplo.
      </p>
    );
  }

  /* O menor piso da régua é o que tira alguém de "faixa nenhuma". Dizê-lo pelo
     nome evita que o salto pareça um erro de digitação. */
  const menorFloor = Math.min(
    ...ladderRungs
      .filter((l) => shapeProblem(l) === null)
      .map((l) => Number(l.platesMin.trim())),
  );
  const isFirstTier = floor === menorFloor;
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <p>
        Quem fechar o mês com exatamente{' '}
        <strong className="font-medium">{floor} placas</strong>, todas comuns, recebe{' '}
        <strong className="font-medium tabular-nums">{format(noFloor)}</strong>.
      </p>
      {oneLess !== null && (
        <p className="text-texto-suave">
          Com {floor - 1} placas seriam{' '}
          <span className="tabular-nums">{format(oneLess)}</span> — ou seja, a{' '}
          <strong className="font-medium text-texto">{floor}ª placa vale </strong>
          <strong className="font-medium tabular-nums text-texto">
            {format(subtract(noFloor, oneLess))}
          </strong>
          {isFirstTier
            ? ' — é ela que tira o consultor de faixa nenhuma e paga todas as anteriores.'
            : '.'}
        </p>
      )}
      <p className="text-xs text-texto-suave">
        Placas Bronze mudam este número: elas contam para escolher a faixa e são pagas por
        fora dela.
      </p>
    </div>
  );
}

/**
 * O caso do Bronze, com os números da régua que está sendo digitada.
 *
 * Ele existe porque o Bronze tem duas propriedades que se confundem, e a
 * confusão custa dinheiro: paga por fora de qualquer faixa **e** conta para
 * escolher a faixa das outras. Cinco placas Bronze num consultor de vinte
 * placas comuns valem R$ 275,00 na régua do sistema antigo, e não R$ 75,00 —
 * sem elas o mesmo consultor teria vinte placas, faixa nenhuma e R$ 0,00.
 *
 * Os números abaixo saem da mesma função que apura, com a régua desta tela: se
 * ela ainda não tem degrau comum que chegue a 25, o exemplo mostra isso em vez
 * de repetir o número do sistema antigo.
 */
function BronzeExample({ rule }: { rule: CommissionRule }) {
  let withBronze: Money;
  let withoutBronze: Money;
  try {
    withBronze = settleCommission(
      [...platesFicticias(20, 'venda', 'v'), ...platesFicticias(5, 'bronze', 'b')],
      'externo',
      rule,
    ).total;
    withoutBronze = settleCommission(
      platesFicticias(20, 'venda', 'v'),
      'externo',
      rule,
    ).total;
  } catch {
    return null;
  }

  return (
    <div className="border-t border-borda px-4 py-4 text-sm">
      <p className="font-medium">Nesta régua, cinco placas Bronze valem o quê?</p>
      <p className="mt-1 text-texto-suave">
        Um consultor com{' '}
        <strong className="font-medium text-texto">20 placas comuns</strong> e{' '}
        <strong className="font-medium text-texto">5 Bronze</strong> recebe{' '}
        <strong className="font-medium tabular-nums text-texto">
          {format(withBronze)}
        </strong>
        . O mesmo consultor sem as cinco Bronze — 20 placas — recebe{' '}
        <span className="tabular-nums">{format(withoutBronze)}</span>. As cinco Bronze
        valeram{' '}
        <strong className="font-medium tabular-nums text-texto">
          {format(subtract(withBronze, withoutBronze))}
        </strong>
        , e não cinco vezes o valor delas: além de pagarem por fora, elas empurram o total
        para dentro de uma faixa.
      </p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="flex flex-col gap-3" aria-busy>
      <span className="esqueleto block h-16 w-full rounded-borda" />
      <span className="esqueleto block h-40 w-full rounded-borda" />
      <span className="esqueleto block h-40 w-full rounded-borda" />
      <span className="sr-only">Carregando a régua desta competência…</span>
    </div>
  );
}
